import Image from "./Image";
import { buildTileUrl } from "./tileUrl";
import type { ImageLoadPayload, ImagePosition, Quality } from "./types";

interface Props {
  quality: Quality;
  imagePosition: ImagePosition;
  baseUrl: string;
  extension: string;
  viewportWidth: number;
  viewportHeight: number;
  onLoad?: (payload: ImageLoadPayload) => void;
  onError?: () => void;
}

const imageCount: Record<Quality, [number, number]> = {
  low: [1, 1],
  medium: [5, 5],
  high: [10, 10],
};

const zIndexes: Record<Quality, number> = {
  low: 1,
  medium: 2,
  high: 3,
};

export default function ImageLayer({
  quality,
  imagePosition,
  baseUrl,
  extension,
  viewportWidth,
  viewportHeight,
  onLoad,
  onError,
}: Props) {
  const [xImageCount, yImageCount] = imageCount[quality];
  const zIndex = zIndexes[quality];
  const currentWidth = imagePosition.bottomRight.x - imagePosition.topLeft.x;
  const currentHeight = imagePosition.bottomRight.y - imagePosition.topLeft.y;

  // Resolution guards on rendered (zoomed) width — mirror TASK-8586.
  if (
    quality === "medium" &&
    (currentWidth < 1200 || currentWidth > 15000 || Number.isNaN(currentWidth))
  ) {
    return null;
  }
  if (
    quality === "high" &&
    (currentWidth < 3000 || Number.isNaN(currentWidth))
  ) {
    return null;
  }

  const xPercent = 1 / xImageCount;
  const yPercent = 1 / yImageCount;

  const tiles = [];
  for (let i = 0; i < xImageCount; i++) {
    for (let j = 0; j < yImageCount; j++) {
      const topLeft = {
        x: imagePosition.topLeft.x + i * xPercent * currentWidth,
        y: imagePosition.topLeft.y + j * yPercent * currentHeight,
      };
      const bottomRight = {
        x: imagePosition.topLeft.x + (i + 1) * xPercent * currentWidth,
        y: imagePosition.topLeft.y + (j + 1) * yPercent * currentHeight,
      };

      const isInView =
        topLeft.x >= -xPercent * currentWidth &&
        topLeft.y >= -yPercent * currentHeight &&
        bottomRight.x <= viewportWidth + xPercent * currentWidth &&
        bottomRight.y <= viewportHeight + yPercent * currentHeight;
      if (!isInView && quality !== "low") continue;

      tiles.push(
        <Image
          key={`${quality}_${i}_${j}`}
          imagePosition={{ topLeft, bottomRight }}
          imageSrc={buildTileUrl(baseUrl, extension, quality, i, j)}
          zIndex={zIndex}
          onLoad={quality === "low" ? onLoad : undefined}
          onError={quality === "low" ? onError : undefined}
        />
      );
    }
  }
  return <>{tiles}</>;
}
